// The model's twelve condition groups as body systems, in the order the wheel
// draws them clockwise from twelve o'clock. This is the same mapping the
// ProteoRisk PDF report uses. A condition group that is not listed here stops
// the report with an explicit error instead of being drawn without a name.
//
// Icons are 24 x 24 line drawings (stroked, round caps). Each entry is a list
// of shapes: { d } for a path, { cx, cy, r, fill } for a circle, with an
// optional transform.

export const BODY_SYSTEMS = Object.freeze([
  {
    code: 'circulatory',
    category: 'Circulatory system',
    name: 'Heart and blood vessels',
    lines: ['Heart and', 'blood vessels'],
  },
  {
    code: 'respiratory',
    category: 'Respiratory system',
    name: 'Lungs and breathing',
    lines: ['Lungs and', 'breathing'],
  },
  {
    code: 'metabolic',
    category: 'Endocrine, nutritional & metabolic',
    name: 'Metabolism and hormones',
    lines: ['Metabolism', 'and hormones'],
  },
  {
    code: 'digestive',
    category: 'Digestive system',
    name: 'Digestion',
    lines: ['Digestion'],
  },
  {
    code: 'genitourinary',
    category: 'Genitourinary system',
    name: 'Urinary and reproductive system',
    lines: ['Urinary and', 'reproductive'],
  },
  {
    code: 'musculoskeletal',
    category: 'Musculoskeletal system',
    name: 'Bones, joints and muscles',
    lines: ['Bones, joints', 'and muscles'],
  },
  {
    code: 'blood',
    category: 'Blood & immune mechanism',
    name: 'Blood and immunity',
    lines: ['Blood and', 'immunity'],
  },
  {
    code: 'infections',
    category: 'Infectious & parasitic diseases',
    name: 'Infections',
    lines: ['Infections'],
  },
  {
    code: 'neoplasms',
    category: 'Neoplasms',
    name: 'Tumours and growths',
    lines: ['Tumours and', 'growths'],
  },
  {
    code: 'eye',
    category: 'Eye & adnexa',
    name: 'Eyes',
    lines: ['Eyes'],
  },
  {
    code: 'skin',
    category: 'Skin & subcutaneous tissue',
    name: 'Skin',
    lines: ['Skin'],
  },
  {
    code: 'multisystem',
    category: 'Multisystem / composite',
    name: 'Several body systems',
    lines: ['Several body', 'systems'],
  },
].map((system, order) => Object.freeze({ ...system, order })));

export const SYSTEM_BY_CATEGORY = new Map(BODY_SYSTEMS.map((system) => [system.category, system]));
export const SYSTEM_BY_CODE = new Map(BODY_SYSTEMS.map((system) => [system.code, system]));

// A bone lying across the icon: two round knobs at each end of a straight
// shaft, drawn horizontally and turned 45 degrees.
const BONE = 'M7.75 10.9H16.25A2.2 2.2 0 1 1 19.41 12A2.2 2.2 0 1 1 16.25 13.1H7.75'
  + 'A2.2 2.2 0 1 1 4.59 12A2.2 2.2 0 1 1 7.75 10.9Z';

export const ICONS = Object.freeze({
  circulatory: [
    { d: 'M12 19.6 5.3 12.9a4.4 4.4 0 0 1 0-6.2 4.4 4.4 0 0 1 6.2 0l.5.5.5-.5a4.4 4.4 0 0 1 6.2 0 4.4 4.4 0 0 1 0 6.2z' },
    { d: 'M6.2 12.2h2.6l1.4-2.6 2.2 4.4 1.5-2.9h3.9' },
  ],
  respiratory: [
    { d: 'M12 4v7.4' },
    { d: 'M12 10.2c-.7 1.3-1.6 2-2.6 2.3M12 10.2c.7 1.3 1.6 2 2.6 2.3' },
    { d: 'M8.9 7.1C6.4 7.8 4.5 11.8 4.5 16c0 2.1 1 3.4 2.7 3.4 1.8 0 2.8-1.5 2.8-3.5V8.8c0-1.1-.4-1.8-1.1-1.7z' },
    { d: 'M15.1 7.1c2.5.7 4.4 4.7 4.4 8.9 0 2.1-1 3.4-2.7 3.4-1.8 0-2.8-1.5-2.8-3.5V8.8c0-1.1.4-1.8 1.1-1.7z' },
  ],
  metabolic: [
    { cx: 8.6, cy: 14.6, r: 4.6 },
    { cx: 16.4, cy: 7.8, r: 3.1 },
    { cx: 17.4, cy: 16.8, r: 2.1 },
  ],
  digestive: [
    { d: 'M9 2.8v3.4c0 2.2-4 3.2-4 7.4s3.2 7 7.4 7c2.8 0 5-1.2 6.2-3.2l2.2.8' },
    { d: 'M12 2.8v4c0 3.8 1.4 6.4 4.6 7 1.4.3 2.4 1.5 2 3.6' },
  ],
  genitourinary: [
    { d: 'M12.6 3.8c-3.8 0-7.1 3.6-7.1 8.2s3.3 8.2 7.1 8.2c2.5 0 4.1-1.7 4.1-3.8 0-1.9-1.7-2.6-1.7-4.4s1.7-2.5 1.7-4.4c0-2.1-1.6-3.8-4.1-3.8z' },
    { d: 'M15.2 12h2.2c1 0 1.8.8 1.8 1.8v6.7' },
  ],
  musculoskeletal: [
    { d: BONE, transform: 'rotate(-45 12 12)' },
  ],
  blood: [
    { d: 'M12 3.4c3.2 4.2 6 7.3 6 10.7a6 6 0 0 1-12 0c0-3.4 2.8-6.5 6-10.7z' },
    { d: 'M9.2 14.4a2.8 2.8 0 0 0 2.8 2.8' },
  ],
  infections: [
    { d: 'M12 3.4l7 2.8v5.2c0 4.4-2.9 7.9-7 9.1-4.1-1.2-7-4.7-7-9.1V6.2z' },
    { d: 'M12 9.2v4.8M9.6 11.6h4.8' },
  ],
  neoplasms: [
    { cx: 12, cy: 12, r: 8.2 },
    { cx: 12, cy: 12, r: 4.6 },
    { cx: 12, cy: 12, r: 1.4, fill: true },
  ],
  eye: [
    { d: 'M2.8 12s3.4-6.1 9.2-6.1 9.2 6.1 9.2 6.1-3.4 6.1-9.2 6.1S2.8 12 2.8 12z' },
    { cx: 12, cy: 12, r: 2.8 },
  ],
  skin: [
    { d: 'M3 7.5c3-2.4 6 2.4 9 0s6-2.4 9 0' },
    { d: 'M3 12.5c3-2.4 6 2.4 9 0s6-2.4 9 0' },
    { d: 'M3 17.5c3-2.4 6 2.4 9 0s6-2.4 9 0' },
  ],
  multisystem: [
    { d: 'M12 7.5 6.8 16M12 7.5 17.2 16M8.8 17.6h6.4' },
    { cx: 12, cy: 5.5, r: 2.6 },
    { cx: 5.5, cy: 17.6, r: 2.6 },
    { cx: 18.5, cy: 17.6, r: 2.6 },
  ],
  // Used by the "how it works" steps on the landing page.
  protein: [
    { d: 'M4.8 19.2 9.6 16.8 6.6 12.4 12.2 10.6 9.8 6 15.6 4.8 19.2 8.6' },
    { cx: 4.8, cy: 19.2, r: 1.9, fill: true },
    { cx: 9.6, cy: 16.8, r: 1.25, fill: true },
    { cx: 6.6, cy: 12.4, r: 1.25, fill: true },
    { cx: 12.2, cy: 10.6, r: 1.25, fill: true },
    { cx: 9.8, cy: 6, r: 1.25, fill: true },
    { cx: 15.6, cy: 4.8, r: 1.25, fill: true },
    { cx: 19.2, cy: 8.6, r: 1.9, fill: true },
  ],
  pattern: [
    { d: 'M3.5 16.5h2.2l1.8-5 2.2 8 2.6-13 2.4 10 1.8-4.5 1.4 4.5h2.6' },
  ],
  conditions: [
    { d: 'M13.4 3.6a8.5 8.5 0 0 1 7 7' },
    { d: 'M20.4 13.4a8.5 8.5 0 0 1-7 7' },
    { d: 'M10.6 20.4a8.5 8.5 0 0 1-7-7' },
    { d: 'M3.6 10.6a8.5 8.5 0 0 1 7-7' },
    { cx: 12, cy: 12, r: 2.6 },
  ],
});
